import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, UserCheck, UserPlus, Clock, ReceiptIndianRupee, Wallet, ArrowRight, Building2,
  ClipboardList, Boxes, MapPin, Activity,
} from "lucide-react";
import api, { inr } from "./api";
import { useAuth } from "./AuthContext";

const ADMIN_ROLES = ["super_admin", "hr_admin", "finance", "project_manager"];

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="bg-[#0C1322] border border-white/10 p-6 card-hover" data-testid={testid}>
    <div className="flex items-center justify-between">
      <Icon size={22} style={{ color: accent }} />
      <span className="mono-label" style={{ color: "#64748B" }}>{label}</span>
    </div>
    <div className="mt-6 font-display font-black text-4xl text-white tracking-tight">{value}</div>
  </div>
);

function EmployeeDashboard({ user }) {
  const now = new Date();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/field/summary", { params: { month: now.getMonth() + 1, year: now.getFullYear() } })
      .then((r) => setSummary(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mt = summary?.material_totals;

  return (
    <div data-testid="employee-dashboard">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <span className="mono-label" style={{ color: "#00F0FF" }}>My Workspace</span>
          <h1 className="mt-2 font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight">
            Welcome, {user?.name?.split(" ")[0] || "Engineer"}
          </h1>
          <p className="text-sm mt-1" style={{ color: "#64748B" }}>Your field activity this month.</p>
        </div>
        <Link to="/portal/field" data-testid="dashboard-log-activity"
          className="group inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-5 py-3 mono-label transition-colors duration-300">
          <ClipboardList size={16} /> Log Today's Activity
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={Activity} label="My Activities" value={summary?.total_activities ?? "—"} accent="#00F0FF" testid="my-activities" />
        <StatCard icon={MapPin} label="Sites Worked" value={summary?.unique_sites ?? "—"} accent="#0066FF" testid="my-sites" />
        <StatCard icon={Boxes} label="Material Used" value={mt ? mt.grand_total : "—"} accent="#22c55e" testid="my-material" />
      </div>

      <div className="mt-4 bg-[#0C1322] border border-white/10 p-6">
        <div className="mono-label mb-3" style={{ color: "#94A3B8" }}>Material Consumed — This Month</div>
        {mt ? (
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-slate-300">
            <span>LC-LC 2M <b className="text-white">{mt.lc_lc.m2}</b></span>
            <span>5M <b className="text-white">{mt.lc_lc.m5}</b></span>
            <span>10M <b className="text-white">{mt.lc_lc.m10}</b></span>
            <span>15/20M <b className="text-white">{mt.lc_lc.m15_20}</b></span>
            <span className="text-slate-500">|</span>
            <span>SC-APC 2M <b className="text-white">{mt.sc_apc.m2}</b></span>
            <span>5M <b className="text-white">{mt.sc_apc.m5}</b></span>
            <span>10M <b className="text-white">{mt.sc_apc.m10}</b></span>
            <span className="text-slate-500">|</span>
            <span>Flexible <b className="text-white">{mt.flexible_mtr}</b> MTR</span>
          </div>
        ) : (
          <p className="text-sm" style={{ color: "#64748B" }}>No activity logged yet this month.</p>
        )}
        <Link to="/portal/field" className="group inline-flex items-center gap-2 mt-6 mono-label hover:text-cyan transition-colors" style={{ color: "#00F0FF" }}>
          Open Field Tracker <ArrowRight size={14} className="cta-arrow" />
        </Link>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/hr/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <span className="mono-label" style={{ color: "#00F0FF" }}>Overview</span>
          <h1 className="mt-2 font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight">Dashboard</h1>
        </div>
        <Link to="/portal/employees/new" data-testid="dashboard-add-employee"
          className="group inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-5 py-3 mono-label transition-colors duration-300">
          <UserPlus size={16} /> Add Employee
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total" value={data?.total_employees ?? "—"} accent="#00F0FF" testid="stat-total" />
        <StatCard icon={UserCheck} label="Active" value={data?.active_employees ?? "—"} accent="#22c55e" testid="stat-active" />
        <StatCard icon={UserPlus} label="New (30d)" value={data?.new_joiners_30d ?? "—"} accent="#0066FF" testid="stat-new" />
        <StatCard icon={Clock} label="On Leave" value={data?.on_leave ?? "—"} accent="#f59e0b" testid="stat-leave" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-1 grid grid-cols-1 gap-4">
          <StatCard icon={ReceiptIndianRupee} label="Payrolls / Month" value={data?.payrolls_this_month ?? "—"} accent="#00F0FF" testid="stat-payrolls" />
          <div className="bg-[#0C1322] border border-white/10 p-6" data-testid="stat-payout">
            <div className="flex items-center justify-between">
              <Wallet size={22} style={{ color: "#00F0FF" }} />
              <span className="mono-label" style={{ color: "#64748B" }}>Monthly Payout (Net)</span>
            </div>
            <div className="mt-6 font-display font-black text-2xl text-white tracking-tight">
              {data ? inr(data.total_monthly_payout) : "—"}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 bg-[#0C1322] border border-white/10 p-6">
          <div className="flex items-center gap-2 mb-5">
            <Building2 size={18} style={{ color: "#00F0FF" }} />
            <span className="mono-label" style={{ color: "#94A3B8" }}>Department Distribution</span>
          </div>
          {data?.department_distribution?.length ? (
            <div className="space-y-3">
              {data.department_distribution.map((d) => {
                const max = Math.max(...data.department_distribution.map((x) => x.count), 1);
                return (
                  <div key={d.department}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-300">{d.department}</span>
                      <span className="text-white font-medium">{d.count}</span>
                    </div>
                    <div className="h-2 bg-[#060A14] border border-white/5">
                      <div className="h-full" style={{ width: `${(d.count / max) * 100}%`, background: "#0066FF" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm" style={{ color: "#64748B" }}>No employees yet. Add your first employee to see stats.</p>
          )}
          <Link to="/portal/employees" className="group inline-flex items-center gap-2 mt-6 mono-label hover:text-cyan transition-colors" style={{ color: "#00F0FF" }}>
            View all employees <ArrowRight size={14} className="cta-arrow" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  if (user && !ADMIN_ROLES.includes(user.role)) return <EmployeeDashboard user={user} />;
  return <AdminDashboard />;
}
